// Hero.tsx
// Enhanced Hero component with hybrid approach:
// - Shows static sports images immediately (no blank hero)
// - Fetches HSPS NFT collection in background
// - Blends NFT images into rotation once loaded

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useInterval from 'use-interval';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useHSPSNFTs } from '../hooks/useHSPSNFTs';
import { nextPinataMediaUrl } from '../web3/ipfs';

type HeroProps = {
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
};

// Static fallback images - shown immediately while NFTs load
const STATIC_IMAGES = [
  {
    url: '/pexels_female_basketball.jpg',
    alt: 'Female basketball player in action',
    isStatic: true,
  },
  {
    url: '/pexels_female_fieldhocky.jpg',
    alt: 'Female athlete playing field hockey',
    isStatic: true,
  },
  {
    url: '/pexels_female_soccer.jpg',
    alt: 'Female soccer player during match',
    isStatic: true,
  },
];

interface HeroImage {
  url: string;
  alt: string;
  isStatic?: boolean;
  isNFT?: boolean;
  nftName?: string;
}

const Hero = ({ title, subtitle, ctaText, ctaLink }: HeroProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<Set<string>>(new Set());
  
  // Fetch HSPS NFTs in background
  const { nfts: hspsNFTs, isLoaded: nftsLoaded, error: nftError } = useHSPSNFTs({
    maxItems: 15,
    shuffle: true,
  });

  // Merge static images with NFT images once loaded
  const images: HeroImage[] = useMemo(() => {
    if (!nftsLoaded || hspsNFTs.length === 0) {
      // Only static images while loading
      return STATIC_IMAGES;
    }

    // Convert NFTs to HeroImage format
    const nftImages: HeroImage[] = hspsNFTs.map((nft) => ({
      url: nft.url,
      alt: nft.alt,
      isNFT: true,
      nftName: nft.name,
    }));

    // Blend strategy: Start with 1 static, then alternate NFTs and remaining statics
    // This creates a nice mix: [static1, nft1, nft2, static2, nft3, nft4, static3, nft5...]
    const blended: HeroImage[] = [];
    
    // Add first static image
    blended.push(STATIC_IMAGES[0]);
    
    // Add first batch of NFTs (60%)
    const firstBatchCount = Math.ceil(nftImages.length * 0.6);
    blended.push(...nftImages.slice(0, firstBatchCount));
    
    // Add remaining static images
    blended.push(...STATIC_IMAGES.slice(1));
    
    // Add remaining NFTs
    blended.push(...nftImages.slice(firstBatchCount));

    return blended;
  }, [hspsNFTs, nftsLoaded]);

  // Preload images
  useEffect(() => {
    images.forEach((image) => {
      const img = new Image();
      img.onload = () => {
        setImagesLoaded((prev) => new Set(prev).add(image.url));
      };
      img.onerror = () => {
        const next = nextPinataMediaUrl(img.src);
        if (next && next !== img.src) img.src = next;
      };
      img.src = image.url;
    });
  }, [images]);

  // Auto-advance slideshow
  useInterval(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }, 5000);

  // Reset index if it exceeds new images length
  useEffect(() => {
    if (currentImageIndex >= images.length) {
      setCurrentImageIndex(0);
    }
  }, [images.length, currentImageIndex]);

  const currentImage = images[currentImageIndex];
  const ctaClassName =
    'sf-ui-system sf-cta inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-secondary to-secondary-600 hover:from-secondary-600 hover:to-secondary-700 text-white font-semibold text-lg rounded-xl shadow-lg shadow-secondary/30 transition-all duration-300 hover:scale-105 hover:shadow-secondary/40';

  // Helper to render title with "Future" highlighted
  const renderTitle = (text: string) => {
    if (text.includes('Future')) {
      const parts = text.split('Future');
      return (
        <>
          {parts[0]}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">
            Future
          </span>
          {parts[1]}
        </>
      );
    }
    return text;
  };

  return (
    <div className="relative h-screen min-h-[600px] max-h-[900px] w-full overflow-hidden">
      {/* Background Image Layer */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentImageIndex}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: 'easeInOut' }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${currentImage.url})`,
            }}
          />
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </motion.div>
      </AnimatePresence>

      {/* NFT Badge - Shows when displaying an NFT */}
      <AnimatePresence>
        {currentImage.isNFT && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.4 }}
            className="absolute top-20 right-2 z-20 max-w-[calc(100vw-1rem)] sm:top-24 sm:right-4 md:top-28 md:right-8"
          >
            <div className="flex max-w-full items-center gap-2 rounded-full border border-white/20 bg-black/60 px-3 py-2 backdrop-blur-md sm:px-4">
              <Sparkles className="h-4 w-4 shrink-0 text-amber-400" />
              <span className="truncate text-xs font-medium text-white/90 sm:text-sm">
                House of Sports Collection
              </span>
            </div>
            {currentImage.nftName && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-white/70 text-xs mt-2 text-right pr-2 max-w-[200px] truncate"
              >
                {currentImage.nftName}
              </motion.p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content Overlay */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 md:px-8">
        {/* Title */}
        <motion.h1
          className="mb-6 max-w-5xl text-3xl font-bold text-white sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {renderTitle(title)}
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-lg md:text-xl lg:text-2xl text-white/90 mb-8 md:mb-10 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {subtitle}
        </motion.p>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {ctaLink.startsWith('/') ? (
            <Link to={ctaLink} className={ctaClassName}>
              {ctaText}
              <ArrowRight className="w-5 h-5" />
            </Link>
          ) : (
            <a href={ctaLink} className={ctaClassName}>
              {ctaText}
              <ArrowRight className="w-5 h-5" />
            </a>
          )}
        </motion.div>

        {/* Secondary info */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-8 text-white/60 text-sm"
        >
          Join athletes already on their journey
        </motion.p>
      </div>

      {/* Image Indicators */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10 flex items-center gap-2">
        {images.slice(0, Math.min(images.length, 12)).map((image, index) => (
          <button
            key={`indicator-${index}`}
            className={`transition-all duration-300 rounded-full ${
              index === currentImageIndex
                ? image.isNFT
                  ? 'w-8 h-2 bg-gradient-to-r from-amber-400 to-orange-500'
                  : 'w-8 h-2 bg-white'
                : 'w-2 h-2 bg-white/50 hover:bg-white/70'
            }`}
            onClick={() => setCurrentImageIndex(index)}
            aria-label={`Go to slide ${index + 1}${image.isNFT ? ' (NFT)' : ''}`}
          />
        ))}
        {images.length > 12 && (
          <span className="text-white/50 text-xs ml-2">+{images.length - 12}</span>
        )}
      </div>

      {/* NFT Collection Status Indicator (subtle, bottom-left) */}
      <AnimatePresence>
        {nftsLoaded && hspsNFTs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, delay: 1 }}
            className="absolute bottom-6 left-4 md:left-8 z-10"
          >
            <div className="flex items-center gap-2 text-white/50 text-xs">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span>{hspsNFTs.length} NFTs loaded</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error indicator (only in development) */}
      {nftError && process.env.NODE_ENV === 'development' && (
        <div className="absolute top-4 left-4 z-20 bg-red-500/80 text-white text-xs px-3 py-1 rounded">
          NFT Error: {nftError}
        </div>
      )}
    </div>
  );
};

export default Hero;
